import { addAndTrack } from "../../../calculators/helpers";
import { findByCode } from "../../../helpers";
import { Green } from "../../../styledCpns/DataDisplay";
import { wpSimpleAnTmaker } from "../config";
import {
  BaneSeries2,
  BlackcliffSeries,
  DragonspineSeries,
  FavoniusSeries,
  LithicSeries,
  RoyalSeries,
  SacrificialSeries,
  WatatsumiSeries,
  DesertSeries
} from "../series";

const PurpleClaymores = [
  {
    code: 145,
    name: "Makhaira Aquamarine",
    icon: "9/90/Weapon_Makhaira_Aquamarine",
    pasvName: "Desert Pavilion",
    ...DesertSeries
  },
  {
    code: 136,
    name: "Sapwood Blade",
    icon: "5/51/Weapon_Forest_Regalia",
    rarity: 4,
    mainStatScale: 44,
    subStat: { type: "Energy Recharge", scale: "6.7%" },
    buffs: [
      {
        index: 0,
        affect: "party",
        addBnes: wpSimpleAnTmaker("ATTRs", "Elemental Mastery", 15),
        desc: ({ wpRfm }) =>
          findByCode(PurpleClaymores, 136).pasvDescs({ wpRfm })[0].buff
      }
    ],
    pasvName: "Forest Sanctuary",
    pasvDescs: ({ wpRfm }) => [
      {
        bonus: (
          <>
            After triggering Burning, Quicken, Aggravate, Spread, Bloom,
            Hyperbloom, or Burgeon, a Leaf of Consciousness will be created
            around the character for a maximum of 10s.
          </>
        ),
        buff: (
          <>
            When picked up, the Leaf will grant the character{" "}
            <Green b>{45 + wpRfm * 15}</Green> <Green>Elemental Mastery</Green>{" "}
            for 12s.
          </>
        ),
        others: (
          <>
            Only 1 Leaf can be generated this way every 20s. This effect can
            still be triggered if the character is not on the field. The Leaf of
            Consciousness' effect cannot stack.
          </>
        )
      }
    ]
  },
  {
    code: 60,
    name: "Snow-Tombed Starsilver",
    icon: "4/49/Weapon_Snow-Tombed_Starsilver",
    rarity: 4,
    mainStatScale: 44,
    subStat: { type: "Physical DMG Bonus", scale: "7.5%" },
    ...DragonspineSeries
  },
  {
    code: 61,
    name: "Sacrificial Greatsword",
    icon: "1/17/Weapon_Sacrificial_Greatsword",
    rarity: 4,
    mainStatScale: 44,
    subStat: { type: "Energy Recharge", scale: "6.7%" },
    ...SacrificialSeries
  },
  {
    code: 62,
    name: "Royal Greatsword",
    icon: "b/bf/Weapon_Royal_Greatsword",
    rarity: 4,
    mainStatScale: 44,
    subStat: { type: "ATK%", scale: "6%" },
    ...RoyalSeries
  },
  {
    code: 63,
    name: "Prototype Archaic",
    icon: "a/ab/Weapon_Prototype_Archaic",
    rarity: 4,
    mainStatScale: 44,
    subStat: { type: "ATK%", scale: "6%" },
    pasvName: "Crush",
    pasvDescs: ({ wpRfm }) => [
      {
        bonus: (
          <>
            On hit, Normal or Charged Attacks have a <Green>50% chance</Green>{" "}
            to deal an additional <Green b>{180 + wpRfm * 60}</Green>{" "}
            <Green>ATK</Green> DMG to opponents within a small AoE. Can only
            occur once every 15s.
          </>
        )
      }
    ]
  },
  {
    code: 64,
    name: "Whiteblind",
    icon: "0/04/Weapon_Whiteblind",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "DEF%", scale: "11.3%" },
    buffs: [
      {
        index: 0,
        affect: "self",
        labels: ["Stacks"],
        inputTypes: ["stacks"],
        values: [4],
        addBnes: ({ ATTRs, wpRfm, inputs, tkDesc, tracker }) => {
          const bnValue = (4.5 + wpRfm * 1.5) * inputs[0];
          addAndTrack(tkDesc, ATTRs, ["ATK%", "DEF%"], bnValue, tracker);
        },
        desc: ({ wpRfm }) =>
          findByCode(PurpleClaymores, 64).pasvDescs({ wpRfm })[0].buff
      }
    ],
    pasvName: "Infusion Blade",
    pasvDescs: ({ wpRfm }) => [
      {
        buff: (
          <>
            On hit, Normal or Charged Attacks increase <Green>ATK</Green> and{" "}
            <Green>DEF</Green> by <Green b>{4.5 + wpRfm * 1.5}%</Green> for 6s.
            Max <Green b>4</Green> stacks. Can only occur once every 0.5s.
          </>
        )
      }
    ]
  },
  {
    code: 65,
    name: "Blackcliff Slasher",
    icon: "d/d7/Weapon_Blackcliff_Slasher",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "CRIT DMG", scale: "12%" },
    ...BlackcliffSeries
  },
  {
    code: 66,
    name: "Lithic Blade",
    icon: "3/3a/Weapon_Lithic_Blade",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "ATK%", scale: "9%" },
    ...LithicSeries
  },
  {
    code: 67,
    name: "Serpent Spine",
    icon: "8/88/Weapon_Serpent_Spine",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "CRIT Rate", scale: "6%" },
    buffs: [
      {
        index: 0,
        affect: "self",
        labels: ["Stacks"],
        inputTypes: ["stacks"],
        values: [5],
        addBnes: ({ hitBnes, wpRfm, inputs, tkDesc, tracker }) => {
          const bnValue = (5 + wpRfm) * inputs[0];
          addAndTrack(tkDesc, hitBnes, "All.pct", bnValue, tracker);
        },
        desc: ({ wpRfm }) => (
          <>{findByCode(PurpleClaymores, 67).pasvDescs({ wpRfm })[0].buff}.</>
        )
      }
    ],
    pasvName: "Wavesplitter",
    pasvDescs: ({ wpRfm }) => [
      {
        buff: (
          <>
            Every 4s a character is on the field, they will deal{" "}
            <Green b>{5 + wpRfm}%</Green> <Green>more DMG</Green> and take{" "}
            {[0, 3, 2.7, 2.4, 2.2, 2][wpRfm]}% more DMG. This effect has a
            maximum of <Green b>5</Green> stacks
          </>
        ),
        others: (
          <>
            and will not be reset if the character leaves the field, but will be
            reduced by 1 stack when the character takes DMG.
          </>
        )
      }
    ]
  },
  {
    code: 68,
    name: "Akuoumaru",
    icon: "c/c5/Weapon_Akuoumaru",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "ATK%", scale: "9%" },
    ...WatatsumiSeries
  },
  {
    code: 69,
    name: "Katsuragikiri Nagamasa",
    icon: "2/2e/Weapon_Katsuragikiri_Nagamasa",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "Energy Recharge", scale: "10%" },
    addBnes: wpSimpleAnTmaker("hitBnes", "ES.pct", 1.5),
    pasvName: "Samurai Conduct",
    pasvDescs: ({ wpRfm }) => [
      {
        bonus: (
          <>
            Increases <Green>Elemental Skill DMG</Green> by{" "}
            <Green b>{4.5 + wpRfm * 1.5}%</Green>. After Elemental Skill hits an
            opponent, the character loses 3 Energy but regenerates{" "}
            <Green b>{2.5 + wpRfm * 0.5}</Green> <Green>Energy</Green> every 2s
            for the next 6s. This effect can occur once every 10s. Can be
            triggered even when the character is not on the field.
          </>
        )
      }
    ]
  },
  {
    code: 70,
    name: "The Bell",
    icon: "6/6e/Weapon_The_Bell",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "HP%", scale: "9%" },
    buffs: [
      {
        index: 0,
        affect: "self",
        addBnes: wpSimpleAnTmaker("hitBnes", "All.pct", 3),
        desc: ({ wpRfm }) =>
          findByCode(PurpleClaymores, 70).pasvDescs({ wpRfm })[0].buff
      }
    ],
    pasvName: "Rebellious Guardian",
    pasvDescs: ({ wpRfm }) => [
      {
        bonus: (
          <>
            Taking DMG generates a shield which absorbs DMG up to{" "}
            {17 + wpRfm * 3}% of max HP. This shield lasts for 10s or until
            broken, and can only be triggered once every 45s.
          </>
        ),
        buff: (
          <>
            While protected by a shield, the character gains{" "}
            <Green b>{9 + wpRfm * 3}%</Green> <Green>increased DMG</Green>.
          </>
        )
      }
    ]
  },
  {
    code: 71,
    name: "Rainslasher",
    icon: "d/d4/Weapon_Rainslasher",
    rarity: 4,
    mainStatScale: 42,
    subStat: { type: "Elemental Mastery", scale: 36 },
    ...BaneSeries2("Storm and Tide", "Hydro or Electro")
  },
  {
    code: 72,
    name: "Luxurious Sea-Lord",
    icon: "a/ab/Weapon_Luxurious_Sea-Lord",
    rarity: 4,
    mainStatScale: 41,
    subStat: { type: "ATK%", scale: "12%" },
    addBnes: wpSimpleAnTmaker("hitBnes", "EB.pct", 3),
    pasvName: "Oceanic Victory",
    pasvDescs: ({ wpRfm }) => [
      {
        bonus: (
          <>
            Increases <Green>Elemental Burst DMG</Green> by{" "}
            <Green b>{9 + wpRfm * 3}%</Green>.
          </>
        ),
        others: (
          <>
            When Elemental Burst hits opponents, there is a 100% chance of
            summoning a titanic tuna that charges and deals{" "}
            <Green b>{75 + wpRfm * 25}%</Green> <Green>ATK</Green> as AoE DMG.
            This effect can occur once every 15s.
          </>
        )
      }
    ]
  },
  {
    code: 73,
    name: "Favonius Greatsword",
    icon: "9/9c/Weapon_Favonius_Greatsword",
    rarity: 4,
    mainStatScale: 41,
    subStat: { type: "Energy Recharge", scale: "13.3%" },
    ...FavoniusSeries
  }
];

export default PurpleClaymores;
